import { useEffect, useState } from "react";
import { Plug, ShieldCheck, RefreshCw, Send, Trash2, AlertTriangle, CheckCircle2, FlaskConical, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";

const STATUS = {
  not_configured: { bg: "var(--surface-alt)", fg: "var(--text-muted)", icon: Plug,
    label: { de: "Nicht konfiguriert", fr: "Non configuré", it: "Non configurato", en: "Not configured" } },
  not_connected: { bg: "#FEF3C7", fg: "#B45309", icon: AlertTriangle,
    label: { de: "Nicht verbunden", fr: "Non connecté", it: "Non connesso", en: "Not connected" } },
  connected: { bg: "#DCFCE7", fg: "#15803D", icon: CheckCircle2,
    label: { de: "Verbunden", fr: "Connecté", it: "Connesso", en: "Connected" } },
  error: { bg: "#FEE2E2", fg: "#B91C1C", icon: AlertTriangle,
    label: { de: "Verbindung fehlgeschlagen", fr: "Échec de connexion", it: "Connessione fallita", en: "Connection failed" } },
};

export function ConnectorPanel({ portalKey }) {
  const { L } = useI18n();
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ site_url: "", username: "", app_password: "" });
  const [busy, setBusy] = useState("");
  const [m3a, setM3a] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/control/connectors/${portalKey}`);
    setConn(data);
    setForm((f) => ({ site_url: data.site_url || "", username: data.username || "", app_password: "" }));
  };
  useEffect(() => { setConn(null); load(); /* eslint-disable-next-line */ }, [portalKey]);

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const save = () => act(async () => {
    const body = { site_url: form.site_url.trim(), username: form.username.trim() };
    if (form.app_password) body.app_password = form.app_password;
    await api.put(`/control/connectors/${portalKey}`, body);
    toast.success(L({ de: "Gespeichert", fr: "Enregistré", it: "Salvato", en: "Saved" }));
    await load();
  }, "save");

  const test = () => act(async () => {
    const { data } = await api.post(`/control/connectors/${portalKey}/test`);
    if (data.status === "connected") toast.success(L({ de: "Verbindung erfolgreich", fr: "Connexion réussie", it: "Connessione riuscita", en: "Connection OK" }));
    else toast.error(data.detail || L({ de: "Verbindung fehlgeschlagen", fr: "Échec", it: "Fallita", en: "Failed" }));
    await load();
  }, "test");

  const sync = () => act(async () => {
    const { data } = await api.post(`/control/connectors/${portalKey}/sync`);
    toast.success(L({
      de: `${data.pushed} übertragen, ${data.failed} fehlgeschlagen`,
      fr: `${data.pushed} envoyés, ${data.failed} échoués`,
      it: `${data.pushed} inviati, ${data.failed} falliti`,
      en: `${data.pushed} pushed, ${data.failed} failed`,
    }));
    await load();
  }, "sync");

  const remove = () => act(async () => {
    await api.delete(`/control/connectors/${portalKey}`);
    toast.success(L({ de: "Verbindung entfernt", fr: "Supprimé", it: "Rimosso", en: "Removed" }));
    await load();
  }, "delete");

  const m3aCreate = () => act(async () => {
    const { data } = await api.post(`/control/connectors/${portalKey}/m3a-test-provider`);
    setM3a(data.provider);
    toast.success(data.created
      ? L({ de: "Test-Anbieter angelegt", fr: "Fournisseur test créé", it: "Fornitore test creato", en: "Test provider created" })
      : L({ de: "Test-Anbieter bereits vorhanden", fr: "Déjà existant", it: "Già esistente", en: "Test provider exists" }));
  }, "m3a-create");

  const m3aPush = () => act(async () => {
    const { data } = await api.post(`/control/connectors/${portalKey}/push-one/${m3a.id}`);
    setPushResult(data);
    if (data.status === "synced") toast.success(L({ de: `Listing #${data.listing_id} erstellt`, fr: `Listing #${data.listing_id}`, it: `Listing #${data.listing_id}`, en: `Listing #${data.listing_id} created` }));
    else toast.error(data.detail || L({ de: "Push fehlgeschlagen", fr: "Échec", it: "Fallito", en: "Push failed" }));
    await load();
  }, "m3a-push");

  const m3aRemove = () => act(async () => {
    await api.delete(`/control/connectors/${portalKey}/m3a-test-provider?purge_wp=true`);
    setM3a(null); setPushResult(null);
    toast.success(L({ de: "Test-Anbieter entfernt", fr: "Supprimé", it: "Rimosso", en: "Test provider removed" }));
    await load();
  }, "m3a-remove");

  if (!conn) return <Loader />;
  const s = STATUS[conn.status] || STATUS.not_configured;
  const SIcon = s.icon;
  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  const configured = conn.has_credentials;

  return (
    <div className="space-y-5" data-testid="connector-panel">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Plug className="h-5 w-5" style={{ color: "var(--accent)" }} />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold" style={{ color: "var(--text)" }}>WordPress / Directorist</div>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {L({ de: "Push: freigegebene Anbieter → WordPress-Einträge. Zugangsdaten werden verschlüsselt gespeichert und nie angezeigt.", fr: "Push : prestataires approuvés → entrées WordPress. Identifiants chiffrés, jamais affichés.", it: "Push: fornitori approvati → voci WordPress. Credenziali cifrate, mai mostrate.", en: "Push: approved providers → WordPress listings. Credentials are encrypted and never shown." })}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" data-testid="connector-status" style={{ background: s.bg, color: s.fg }}>
            <SIcon className="h-3.5 w-3.5" /> {L(s.label)}
          </span>
        </div>
        {(conn.last_sync_at || conn.last_error || conn.wp_user) && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
            {conn.wp_user && <div><span className="uppercase tracking-wider">WP-User</span><div style={{ color: "var(--text)" }}>{conn.wp_user}</div></div>}
            <div><span className="uppercase tracking-wider">{L({ de: "Letzter Sync", fr: "Dernier sync", it: "Ultimo sync", en: "Last sync" })}</span><div style={{ color: "var(--text)" }}>{conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : "—"} {conn.synced_count ? `· ${conn.synced_count}` : ""}</div></div>
            {conn.last_error && <div className="sm:col-span-3"><span className="uppercase tracking-wider" style={{ color: "#B91C1C" }}>{L({ de: "Fehler", fr: "Erreur", it: "Errore", en: "Error" })}</span><div style={{ color: "#B91C1C" }}>{conn.last_error}</div></div>}
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>{L({ de: "Zugangsdaten", fr: "Identifiants", it: "Credenziali", en: "Credentials" })}</div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Site-URL (https://)</label>
          <input data-testid="connector-siteurl" placeholder="https://elenya.ch" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
            value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Benutzername", fr: "Utilisateur", it: "Utente", en: "Username" })}</label>
            <input data-testid="connector-username" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
              value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Application Password</label>
            <input data-testid="connector-password" type="password" autoComplete="new-password"
              placeholder={configured ? "•••• (gespeichert)" : "xxxx xxxx xxxx xxxx"}
              className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
              value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Btn onClick={save} disabled={busy === "save" || !form.site_url || !form.username} data-testid="connector-save">
            <ShieldCheck className="h-4 w-4" /> {L({ de: "Speichern", fr: "Enregistrer", it: "Salva", en: "Save" })}
          </Btn>
          <Btn variant="outline" onClick={test} disabled={busy === "test" || !configured} data-testid="connector-test">
            <RefreshCw className={`h-4 w-4 ${busy === "test" ? "animate-spin" : ""}`} /> {L({ de: "Verbindung testen", fr: "Tester", it: "Testa", en: "Test connection" })}
          </Btn>
          <Btn variant="soft" onClick={sync} disabled={busy === "sync" || conn.status !== "connected"} data-testid="connector-sync">
            <Send className="h-4 w-4" /> {L({ de: "Jetzt syncen", fr: "Synchroniser", it: "Sincronizza", en: "Sync now" })}
          </Btn>
          {configured && (
            <Btn variant="danger" onClick={remove} disabled={busy === "delete"} className="ml-auto" data-testid="connector-delete">
              <Trash2 className="h-4 w-4" />
            </Btn>
          )}
        </div>
      </Card>

      <Card className="p-5 space-y-3" data-testid="m3a-test-card">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4" style={{ color: "var(--accent)" }} />
          <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
            {L({ de: "M3-A Live-Test (genau 1 Test-Anbieter)", fr: "Test live M3-A (1 fournisseur test)", it: "Test live M3-A (1 fornitore test)", en: "M3-A live test (exactly 1 test provider)" })}
          </div>
        </div>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {L({ de: "Legt einen klar markierten Test-Anbieter 'M3-A Testeintrag' an und pusht genau 1 Directorist-Listing. Es werden nur vorhandene Kategorie-/Standort-Terme verwendet (kein automatisches Anlegen). Erst nutzbar, wenn die Verbindung verbunden ist.", fr: "Crée un fournisseur test et pousse 1 listing. Termes existants uniquement.", it: "Crea un fornitore test e invia 1 listing. Solo termini esistenti.", en: "Creates a clearly-marked test provider and pushes exactly 1 Directorist listing using only existing category/location terms." })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Btn variant="outline" onClick={m3aCreate} disabled={busy === "m3a-create"} data-testid="m3a-create">
            <FlaskConical className="h-4 w-4" /> {L({ de: "Test-Anbieter anlegen/prüfen", fr: "Créer/vérifier", it: "Crea/verifica", en: "Create/check test provider" })}
          </Btn>
          <Btn variant="soft" onClick={m3aPush} disabled={busy === "m3a-push" || !m3a || conn.status !== "connected"} data-testid="m3a-push">
            <Send className="h-4 w-4" /> {L({ de: "1 Test-Listing pushen", fr: "Pousser 1 listing", it: "Invia 1 listing", en: "Push 1 test listing" })}
          </Btn>
          {m3a && (
            <Btn variant="danger" onClick={m3aRemove} disabled={busy === "m3a-remove"} className="ml-auto" data-testid="m3a-remove">
              <Trash2 className="h-4 w-4" /> {L({ de: "Test entfernen", fr: "Supprimer", it: "Rimuovi", en: "Remove test" })}
            </Btn>
          )}
        </div>
        {m3a && (
          <div className="text-xs rounded-lg border p-2.5" data-testid="m3a-provider-info" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
            <div><b style={{ color: "var(--text)" }}>{m3a.business_name}</b> · {L({ de: "Kategorie", fr: "Catégorie", it: "Categoria", en: "Category" })}: {m3a.category || "—"} · {L({ de: "Standort", fr: "Lieu", it: "Località", en: "Location" })}: {m3a.location || "—"}</div>
            <div className="font-mono">id: {m3a.id}</div>
          </div>
        )}
        {pushResult && pushResult.status === "synced" && (
          <div className="text-xs rounded-lg border p-2.5 space-y-1" data-testid="m3a-push-result" style={{ borderColor: "rgba(34,197,94,0.4)", color: "#15803D" }}>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Directorist Listing #{pushResult.listing_id}</div>
            {pushResult.permalink && <a href={pushResult.permalink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline break-all"><ExternalLink className="h-3 w-3" /> {pushResult.permalink}</a>}
            {pushResult.warnings?.length > 0 && (
              <ul className="list-disc pl-4" style={{ color: "#B45309" }}>{pushResult.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            )}
          </div>
        )}
        {pushResult && pushResult.status === "error" && (
          <div className="text-xs rounded-lg border p-2.5" data-testid="m3a-push-error" style={{ borderColor: "rgba(239,68,68,0.4)", color: "#B91C1C" }}>
            <AlertTriangle className="inline h-4 w-4 mr-1" /> {pushResult.detail}
          </div>
        )}
      </Card>
    </div>
  );
}
